import React, { useEffect, useState, useCallback } from "react";
import FirebaseService from "./FirebaseService";
import siteSettings from "../config/siteSettings";
import "./Hero.css";

const Hero = () => {
  const keyTechIcons = [
    { title: "React", src: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", alt: "React" },
    { title: "TypeScript", src: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg", alt: "TypeScript" },
    { title: "Node.js", src: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", alt: "Node.js" },
    { title: "JavaScript", src: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg", alt: "JavaScript" },
    { title: "Python", src: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", alt: "Python" },
    { title: "Git", src: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/git/git-original.svg", alt: "Git" },
    { title: "NPM", src: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/npm/npm-original-wordmark.svg", alt: "NPM" },
    { title: "Vite", src: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vitejs/vitejs-original.svg", alt: "Vite" },
  ];

  const [scrollY, setScrollY] = useState(0);
  const [talkBtnOpacity, setTalkBtnOpacity] = useState(1);
  const [isHireModalOpen, setIsHireModalOpen] = useState(false);
  const [hireForm, setHireForm] = useState({
    name: "",
    email: "",
    projectType: "",
    budget: "",
    timeline: "",
    message: "",
  });
  const [isSubmittingHire, setIsSubmittingHire] = useState(false);
  const [hireSubmitSuccess, setHireSubmitSuccess] = useState("");
  const [hireSubmitError, setHireSubmitError] = useState("");

  const updateTalkBtnOpacity = useCallback(() => {
    const contactEl = document.getElementById("contact");
    if (!contactEl) {
      setTalkBtnOpacity(1);
      return;
    }
    const rect = contactEl.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    const startFade = vh * 0.9;
    const endFade = vh * 0.08;
    let op = 1;
    if (rect.top < startFade) {
      op = rect.top <= endFade ? 0 : (rect.top - endFade) / (startFade - endFade);
    }
    setTalkBtnOpacity(Math.max(0, Math.min(1, op)));
  }, []);

  const handleScroll = useCallback(() => {
    setScrollY(window.scrollY);
    updateTalkBtnOpacity();
  }, [updateTalkBtnOpacity]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", updateTalkBtnOpacity, { passive: true });
    updateTalkBtnOpacity();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateTalkBtnOpacity);
    };
  }, [handleScroll, updateTalkBtnOpacity]);

  useEffect(() => {
    const openHireModal = () => setIsHireModalOpen(true);
    window.addEventListener("openHireModal", openHireModal);
    return () => window.removeEventListener("openHireModal", openHireModal);
  }, []);

  // Calculate opacity and scale based on scroll position
  const opacity = 1 - Math.min(scrollY / 500, 1); // Adjust 500 to control when fading starts
  const scale = 1 + Math.min(scrollY / 1000, 1); // Adjust 1000 to control the zooming speed
  const scaleout = 1 + Math.max(scrollY / 1000, 0); // Adjust 1000 to control the zooming speed
  const heroIsActive = scrollY < 520;
  const iconStyle = {
    bottom: `${Math.max(50 - scrollY / 5, 10)}px`,
  };

  const handleHireInputChange = (event) => {
    const { name, value } = event.target;
    setHireForm((prev) => ({ ...prev, [name]: value }));
    if (hireSubmitError) {
      setHireSubmitError("");
    }
  };

  const handleHireSubmit = async (event) => {
    event.preventDefault();

    setIsSubmittingHire(true);
    setHireSubmitError("");
    setHireSubmitSuccess("");

    try {
      await FirebaseService.submitHireLead(hireForm);
      setHireSubmitSuccess("Thanks! Your inquiry was submitted successfully.");
      setHireForm({
        name: "",
        email: "",
        projectType: "",
        budget: "",
        timeline: "",
        message: "",
      });
    } catch (error) {
      setHireSubmitError(error.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmittingHire(false);
    }
  };

  const isClassicHero = siteSettings.heroVariant === "classic";

  return (
    <div className={`hero ${heroIsActive ? "" : "hero-past"}`}>
      <div className="base_section">
        {isClassicHero && (
          <div className="main-titles" style={{ opacity, transform: `scale(${scale})` }}>
            <h1 className="sr-only">Sunil Anand - Full-Stack Architect &amp; AI-Driven Systems Builder</h1>
            <div className="title title_1">
              <span className="stroke">WEB</span>
              <span>DEVELOPER</span>
            </div>
            <div className="title title_2">
              <span>FULL STACK</span>
              <span className="stroke">ARCHITECT</span>
            </div>
            <div className="title title_3">
              <span className="stroke">AI-POWERED</span>
              <span>APPLICATIONS</span>
            </div>
            <div className="title title_4">
              <span>ACCESSIBILITY</span>
              <span className="stroke">FIRST</span>
            </div>
            <div className="title title_5">
              <span className="stroke">CODE</span>
              <span>UNIQUELY</span>
            </div>
          </div>
        )}
      </div>
      {!isClassicHero && (
        <>
          <div className="hero-bg-visual" style={{ opacity }} aria-hidden="true">
            <div
              className="hero-bg-visual-layer"
              style={{ backgroundImage: "url(/images/hero-ai-vortex.png)" }}
            />
          </div>
          <div className="hero-content hero-modern-layout" style={{ opacity, transform: `scale(${scale})` }}>
            <div className="hero-copy">
              <div className="hero-main-row">
                <div className="hero-lead">
                  <h1 className="hero-headline">
                    <span className="hero-name">Sunil Anand</span>
                    <span className="hero-role">Full-Stack Architect &amp; AI-Driven Systems Builder</span>
                  </h1>
                  <p className="hero-subheadline">
                    Building scalable web and AI-powered applications for ed-tech and enterprise platforms.
                  </p>
                  <div className="hero-highlights">
                    <p>22+ years delivering scalable, production-grade systems</p>
                    <p>Built ed-tech platforms with SCORM &amp; global accessibility standards</p>
                    <p>Designing AI-driven architectures with intelligent workflows</p>
                  </div>
                </div>
                <div className="hero-actions">
                  <button
                    type="button"
                    className="hero-action-btn"
                    onClick={() => setIsHireModalOpen(true)}
                  >
                    Hire Me
                  </button>
                  <a href="#projects" className="hero-action-btn hero-action-secondary">View Projects</a>
                  <a href="#contact" className="hero-action-btn hero-action-secondary">Contact Me</a>
                </div>
              </div>
              <p className="hero-trust-signal">Trusted by global teams across US, UK &amp; Middle East</p>
              <p className="hero-proof-signal">Architected systems used by global learners and enterprise teams</p>
            </div>
          </div>
        </>
      )}

      <div className='tech_stack' style={iconStyle}>
        {keyTechIcons.map((icon) => (
          <div key={icon.title} className="tech-icon" style={{ opacity, transform: `scale(${scaleout}) translateY(-${scrollY / 2}px)` }} title={icon.title}>
            <img src={icon.src} alt={icon.alt} />
          </div>
        ))}
      </div>
      <a
        href="#contact"
        className="sticky-talk-btn"
        aria-label="Let's Talk"
        style={{
          opacity: talkBtnOpacity,
          pointerEvents: talkBtnOpacity < 0.04 ? "none" : "auto",
          visibility: talkBtnOpacity < 0.02 ? "hidden" : "visible",
        }}
        aria-hidden={talkBtnOpacity < 0.02}
        tabIndex={talkBtnOpacity < 0.02 ? -1 : undefined}
      >
        Let&apos;s Talk
      </a>

      {isHireModalOpen && (
        <div
          className="hire-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="hire-modal-title"
        >
          <div className="hire-modal-card">
            <div className="hire-modal-header">
              <h2 id="hire-modal-title">Hire Me</h2>
              <button
                type="button"
                className="hire-modal-close"
                aria-label="Close form"
                onClick={() => setIsHireModalOpen(false)}
              >
                x
              </button>
            </div>
            <p className="hire-modal-subtitle">
              Tell me about your project. I&apos;ll get back within 24 hours.
            </p>
            {hireSubmitSuccess && (
              <p className="hire-modal-success" role="status">{hireSubmitSuccess}</p>
            )}
            {hireSubmitError && (
              <p className="hire-modal-error" role="alert">{hireSubmitError}</p>
            )}

            <form className="hire-modal-form" onSubmit={handleHireSubmit}>
              <label>
                Name
                <input
                  type="text"
                  name="name"
                  value={hireForm.name}
                  onChange={handleHireInputChange}
                  required
                />
              </label>

              <label>
                Email
                <input
                  type="email"
                  name="email"
                  value={hireForm.email}
                  onChange={handleHireInputChange}
                  required
                />
              </label>

              <label>
                Project Type
                <select
                  name="projectType"
                  value={hireForm.projectType}
                  onChange={handleHireInputChange}
                  required
                >
                  <option value="">Select project type</option>
                  <option value="Full Web Application (Frontend + Backend)">Full Web Application (Frontend + Backend)</option>
                  <option value="Ed-Tech / Learning Platform">Ed-Tech / Learning Platform</option>
                  <option value="Interactive Tools & Simulations">Interactive Tools & Simulations</option>
                  <option value="LMS / SCORM Integration">LMS / SCORM Integration</option>
                  <option value="Existing System Upgrade / Optimization">Existing System Upgrade / Optimization</option>
                  <option value="MVP / New Product Development">MVP / New Product Development</option>
                  <option value="Other">Other</option>
                </select>
              </label>

              <label>
                Budget Range
                <select
                  name="budget"
                  value={hireForm.budget}
                  onChange={handleHireInputChange}
                >
                  <option value="">Select budget range</option>
                  <option value="Under $3,000 (Basic / Small Tasks)">Under $3,000 (Basic / Small Tasks)</option>
                  <option value="$3,000 – $10,000 (Small Projects)">$3,000 – $10,000 (Small Projects)</option>
                  <option value="$10,000 – $25,000 (Mid-size Builds)">$10,000 – $25,000 (Mid-size Builds)</option>
                  <option value="$25,000 – $75,000 (Advanced Systems)">$25,000 – $75,000 (Advanced Systems)</option>
                  <option value="$75,000+ (Enterprise / Long-term)">$75,000+ (Enterprise / Long-term)</option>
                </select>
              </label>

              <label>
                Project Timeline
                <select
                  name="timeline"
                  value={hireForm.timeline}
                  onChange={handleHireInputChange}
                  required
                >
                  <option value="">Select timeline</option>
                  <option value="ASAP">ASAP</option>
                  <option value="1–2 Months">1–2 Months</option>
                  <option value="3–6 Months">3–6 Months</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </label>

              <label>
                Project Details
                <textarea
                  name="message"
                  rows="4"
                  value={hireForm.message}
                  onChange={handleHireInputChange}
                  required
                />
              </label>

              <button type="submit" className="hero-action-btn" disabled={isSubmittingHire}>
                {isSubmittingHire ? "Sending..." : "Start Project Discussion"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hero;
